import React, { useMemo, useState } from "react";
import { Box, Paper, Skeleton, TableContainer, Typography, Popover } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getTicket } from "../../services/ticket.service";

const CategoryTotalsTable = ({ categoryReportData, loading = false }) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState("closedWithout"); // openWithout | closedWithout | totalWithout | openWithin | closedWithin
  const [sortAsc, setSortAsc] = useState(false);
  const baseRows = useMemo(() => {
    if (categoryReportData && typeof categoryReportData === "object") {
      return Object.entries(categoryReportData).map(([key, value]) => ({
        description: key,
        openWithin: value.open?.within || 0,
        openWithout: value.open?.without || 0,
        closedWithin: value.closed?.within || 0,
        closedWithout: value.closed?.without || 0,
        openWithinTickets: value.open?.withinTicketIds || [],
        openWithoutTickets: value.open?.withoutTicketIds || [],
        closedWithinTickets: value.closed?.withinTicketIds || [],
        closedWithoutTickets: value.closed?.withoutTicketIds || [],
        total:
          (value.open?.within || 0) +
          (value.open?.without || 0) +
          (value.closed?.within || 0) +
          (value.closed?.without || 0),
      }));
    }
    return [];
  }, [categoryReportData]);

  const rows = useMemo(() => {
    const copy = [...baseRows];
    return copy.sort((a, b) => {
      const totalWithoutA = (a.openWithout || 0) + (a.closedWithout || 0);
      const totalWithoutB = (b.openWithout || 0) + (b.closedWithout || 0);
      const openWithinA = a.openWithin || 0;
      const openWithinB = b.openWithin || 0;
      const closedWithinA = a.closedWithin || 0;
      const closedWithinB = b.closedWithin || 0;
      let va = 0;
      let vb = 0;
      if (sortKey === "openWithout") { va = a.openWithout || 0; vb = b.openWithout || 0; }
      else if (sortKey === "closedWithout") { va = a.closedWithout || 0; vb = b.closedWithout || 0; }
      else if (sortKey === "openWithin") { va = openWithinA; vb = openWithinB; }
      else if (sortKey === "closedWithin") { va = closedWithinA; vb = closedWithinB; }
      else { va = totalWithoutA; vb = totalWithoutB; }
      return sortAsc ? va - vb : vb - va;
    });
  }, [baseRows, sortKey, sortAsc]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    if (ticket && ticket._id) {
      try {
        // Fetch full ticket data using getTicket API
        // Search by ticketId for exact match
        const ticketIdToSearch = ticket.ticketId || ticket._id;
        const response = await getTicket({
          search: ticketIdToSearch,
          limit: 100, // Increase limit to ensure we find the ticket
        });
        
        // Find exact match by ticketId or _id
        let fullTicket = null;
        if (response?.data && response.data.length > 0) {
          // First try exact ticketId match
          fullTicket = response.data.find(
            (t) => t.ticketId === ticketIdToSearch
          );
          
          // If not found, try _id match (handle both string and ObjectId)
          if (!fullTicket) {
            fullTicket = response.data.find(
              (t) => {
                const tId = t._id?.toString();
                const ticketId = ticket._id?.toString();
                return tId === ticketId;
              }
            );
          }
          
          // Fallback to first result if exact match not found
          if (!fullTicket && response.data.length > 0) {
            fullTicket = response.data[0];
          }
        }
        
        if (fullTicket) {
          // Remove timer-related fields before navigation
          const {
            assignTimer,
            finalTimer,
            followUpTimer,
            initialTimer,
            timer,
            ...cleanTicket
          } = fullTicket;
          console.log("Navigating with full ticket:", cleanTicket);
          navigate("/feedback", { state: cleanTicket });
        } else {
          console.warn("Full ticket not found, using minimal data");
          // Fallback: navigate with minimal ticket data if fetch fails
          const {
            assignTimer,
            finalTimer,
            followUpTimer,
            initialTimer,
            timer,
            ...cleanTicket
          } = ticket;
          navigate("/feedback", { state: cleanTicket });
        }
      } catch (error) {
        console.error("Error fetching ticket:", error);
        // Fallback: navigate with minimal ticket data if fetch fails
        const {
          assignTimer,
          finalTimer,
          followUpTimer,
          initialTimer,
          timer,
          ...cleanTicket
        } = ticket;
        navigate("/feedback", { state: cleanTicket });
      }
    }
  };

  const TicketCountCell = ({ count, tickets, type, isLast = false, padding = 12 }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handlePopoverOpen = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
      setAnchorEl(null);
    };

    const handleTicketClickInCell = (ticket) => {
      handlePopoverClose();
      handleTicketClick(ticket);
    };

    if (!count || count === 0) {
      return (
        <td style={{
          fontFamily: "Inter", 
          borderRight: isLast ? "none" : "1px solid #f3f4f6", 
          padding: padding, 
          textAlign: "center", 
          color: "#6b7280" 
        }}>
          {count || 0}
        </td>
      );
    }

    return (
      <td style={{
        fontFamily: "Inter", 
        borderRight: isLast ? "none" : "1px solid #f3f4f6", 
        padding: padding, 
        textAlign: "center", 
        color: type === "closed" ? (count > 0 ? "#16a34a" : "#6b7280") : "#6b7280",
        cursor: "pointer",
        position: "relative"
      }}>
        <Box
          aria-owns={open ? "ticket-popover" : undefined}
          aria-haspopup="true"
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          sx={{
            display: "inline-block",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
              color: "#00675d"
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Navigate to first ticket if available
            if (tickets && tickets.length > 0) {
              handleTicketClickInCell(tickets[0]);
            }
          }}
        >
          {count}
        </Box>
        <Popover
          id="ticket-popover"
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          sx={{
            pointerEvents: "none",
            "& .MuiPopover-paper": {
              pointerEvents: "auto",
            },
          }}
          onMouseEnter={() => {
            // Keep popover open when hovering over it
            if (anchorEl) {
              setAnchorEl(anchorEl);
            }
          }}
          onMouseLeave={handlePopoverClose}
        >
          <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, mb: 0.5 }}>
              Ticket IDs (Click to open):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {tickets && tickets.length > 0 ? (
                tickets.map((ticket, idx) => {
                  const ticketId = ticket.ticketId || ticket._id;
                  if (!ticketId) return null;
                  return (
                    <Box
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTicketClickInCell(ticket);
                      }}
                      sx={{
                        fontSize: "11px",
                        padding: "4px 8px",
                        background: "#00675d",
                        color: "#fff",
                        borderRadius: "4px",
                        cursor: "pointer",
                        "&:hover": {
                          background: "#005248",
                          textDecoration: "underline"
                        }
                      }}
                    >
                      {ticketId}
                    </Box>
                  );
                })
              ) : (
                <Typography sx={{ fontSize: "11px", color: "#999" }}>
                  No tickets available
                </Typography>
              )}
            </Box>
          </Box>
        </Popover>
      </td>
    );
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        borderRadius: 2, 
        overflow: "hidden", 
        border: "1px solid", 
        borderColor: "divider",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          borderColor: "#00675d",
        },
      }} 
      className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]"
    >
      <Box sx={{ background: "linear-gradient(90deg, #eff6ff 0%, #dbeafe 100%)", borderBottom: "1px solid", borderColor: "divider", px: 3, pt: 2, pb: "17px" }}>
        <Typography variant="h6" sx={{ fontFamily: "Inter", fontSize: "18px", fontWeight: 600, color: "grey.800", lineHeight: "28px" }}>
          Category Report
        </Typography>
      </Box>
      <TableContainer>
        {loading ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb" }}>
                {[...Array(6)].map((_, i) => (
                  <th key={i} style={{ padding: "8px 12px", borderRight: i < 5 ? "1px solid #e5e7eb" : "none" }}>
                    <Skeleton variant="text" width={100} height={16} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(6)].map((_, r) => (
                <tr key={r}>
                  {[...Array(6)].map((_, c) => (
                    <td key={c} style={{ padding: "12px", borderRight: c < 5 ? "1px solid #f3f4f6" : "none", borderTop: "1px solid #e5e7eb" }}>
                      <Skeleton variant="text" width={c === 0 ? 160 : 40} height={16} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f9fafb" }}>
                <th style={{fontFamily: "Inter", width: 223, fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.6px", lineHeight: "16px", borderRight: "1px solid #e5e7eb", padding: "8px 12px", textAlign: "left" }}>DESCRIPTION</th>
                <th colSpan={2} style={{fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.6px", lineHeight: "16px", borderRight: "1px solid #e5e7eb", padding: 8, textAlign: "center" }}>OPEN</th>
                <th colSpan={2} style={{fontFamily: "Inter", fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.6px", lineHeight: "16px", borderRight: "1px solid #e5e7eb", padding: 8, textAlign: "center" }}>CLOSED</th>
                <th style={{fontFamily: "Inter", width: 94, fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.6px", lineHeight: "16px", padding: "8px 12px", textAlign: "center" }}>TOTAL</th>
              </tr>
              <tr style={{ backgroundColor: "#f9fafb" }}>
                <th style={{fontFamily: "Inter", width: 223, borderRight: "1px solid #e5e7eb", padding: 4 }} />
                <th onClick={() => handleSort("openWithin")} style={{ cursor: "pointer", fontFamily: "Inter", fontSize: 12, color: "#4b5563", borderRight: "1px solid #e5e7eb", padding: 4, textAlign: "center", fontWeight: sortKey === "openWithin" ? 700 : 400 }}>Within{sortKey === "openWithin" ? (sortAsc ? " ▲" : " ▼") : ""}</th>
                <th onClick={() => handleSort("openWithout")} style={{ cursor: "pointer", fontFamily: "Inter", fontSize: 12, color: "#4b5563", borderRight: "1px solid #e5e7eb", padding: 4, textAlign: "center", fontWeight: sortKey === "openWithout" ? 700 : 400 }}>
                  Overdue KPI{sortKey === "openWithout" ? (sortAsc ? " ▲" : " ▼") : ""}
                </th>
                <th onClick={() => handleSort("closedWithin")} style={{ cursor: "pointer", fontFamily: "Inter", fontSize: 12, color: "#4b5563", borderRight: "1px solid #e5e7eb", padding: 4, textAlign: "center", fontWeight: sortKey === "closedWithin" ? 700 : 400 }}>Within{sortKey === "closedWithin" ? (sortAsc ? " ▲" : " ▼") : ""}</th>
                <th onClick={() => handleSort("closedWithout")} style={{ cursor: "pointer", fontFamily: "Inter", fontSize: 12, color: "#4b5563", borderRight: "1px solid #e5e7eb", padding: 4, textAlign: "center", fontWeight: sortKey === "closedWithout" ? 700 : 400 }}>
                  Overdue KPI{sortKey === "closedWithout" ? (sortAsc ? " ▲" : " ▼") : ""}
                </th>
                <th style={{fontFamily: "Inter", width: 94 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr 
                  key={index} 
                  style={{ 
                    backgroundColor: "#fff", 
                    borderTop: "1px solid #e5e7eb",
                    transition: "background-color 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff";
                  }}
                >
                  <td style={{fontFamily: "Inter", fontSize: 14, color: "#111827", lineHeight: "20px", borderRight: "1px solid #f3f4f6", padding: index === 0 ? 8 : 12 }}>{row.description}</td>
                  <TicketCountCell 
                    count={row.openWithin || 0} 
                    tickets={row.openWithinTickets || []} 
                    type="open"
                    padding={index === 0 ? 8 : 12}
                  />
                  <TicketCountCell 
                    count={row.openWithout || 0} 
                    tickets={row.openWithoutTickets || []} 
                    type="open"
                    padding={index === 0 ? 8 : 12}
                  />
                  <TicketCountCell 
                    count={row.closedWithin || 0} 
                    tickets={row.closedWithinTickets || []} 
                    type="closed"
                    padding={index === 0 ? 8 : 12}
                  />
                  <TicketCountCell 
                    count={row.closedWithout || 0} 
                    tickets={row.closedWithoutTickets || []} 
                    type="closed"
                    padding={index === 0 ? 8 : 12}
                  />
                  <td style={{ textAlign: "center", fontFamily: "Inter", fontSize: 14, color: "#111827", padding: index === 0 ? 8 : 12 }}>{row.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableContainer>
    </Paper>
  );
};

export default CategoryTotalsTable;


